// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { InvoiceModel } from "@insite/client-framework/Types/ApiModels";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconInvoice from "@plumberssupply/XDL/Icons/IconInvoice";
import { color, mergeWithCss, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps {
    invoice: InvoiceModel;
    payInvoicesUrl?: string;
    extendedStyles?: InvoiceSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface InvoiceSummaryCardStyles {
    dataTableCells?: DataTableCellBaseProps;
    mobileTitle?: TypographyPresentationProps;
    dataCell?: DataTableCellProps;
    companyNameCell?: DataTableCellProps;
    payInvoiceButtonSpinner?: LoadingSpinnerProps;
    payInvoiceLink?: LinkPresentationProps;
    payInvoiceCell?: DataTableCellProps;
    mobileSeparator?: InjectableCss;
}

export const invoiceSummaryCardStyles: InvoiceSummaryCardStyles = {
    payInvoiceLink: {
        icon: {
            iconProps: {
                src: IconInvoice,
                size: 14,
                css: css`
                    opacity: 0.5;
                `,
            },
        },
        typographyProps: {
            size: 14,
            weight: 800,
        },
    },
    mobileTitle: {
        size: 14,
        weight: 800,
        css: css`
            display: none;
            ${xScreen("lte", "md")} {
                display: inline-block;
                width: 50%;
            }
        `,
    },
    dataCell: {
        css: css`
            font-size: 14px;
            ${xScreen("lte", "md")} {
                display: flex;
                border: none;

                & > span {
                    display: inline-block;
                    width: 50%;
                }
            }
        `,
    },
    companyNameCell: {
        typographyProps: {
            ellipsis: true,
        },
        css: css`
            max-width: 320px;
            ${xScreen("gt", "md")} {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            ${xScreen("lte", "md")} {
                height: 80px;
                max-width: none;
                white-space: pre-wrap;
                overflow: hidden;
            }
        `,
    },
    payInvoiceCell: {
        css: css`
            border-left: 1px solid ${color("common.ui8")};

            ${xScreen("lte", "md")} {
                border-left: none;
                height: inherit;
                border-bottom: 1px solid ${color("common.ui8")};
            }
        `,
    },
    mobileSeparator: {
        css: css`
            display: none;
            border-bottom: 1px solid ${color("common.ui8")};
            margin: 10px 12px 0;

            ${xScreen("lte", "md")} {
                display: block;
            }
        `,
    },
};

const InvoiceSummaryCard: FC<Props> = ({ language, invoice, payInvoicesUrl, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(invoiceSummaryCardStyles, extendedStyles));

    const formatDate = (invoiceDate: any) =>
        getLocalizedDateTime({
            dateTime: new Date(invoiceDate),
            language,
            options: {
                year: "2-digit",
                month: "numeric",
                day: "numeric",
            },
        });
    const invoiceNumber = invoice.invoiceNumber;

    return (
        <>
            <DataTableCell {...styles.dataCell}>
                <Typography {...styles.mobileTitle}>{translate("Date")}</Typography>
                <Typography>{formatDate(invoice.invoiceDate)}</Typography>
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <Typography {...styles.mobileTitle}>{translate("Invoice #")}</Typography>
                <Typography>{invoiceNumber}</Typography>
            </DataTableCell>
            <DataTableCell {...mergeWithCss(styles.dataCell, styles.companyNameCell)}>
                <Typography {...styles.mobileTitle}>{translate("Ship-To / Pick Up")}</Typography>
                <Typography>{`${invoice.stCompanyName || invoice.btCompanyName || "--"}`}</Typography>
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <Typography {...styles.mobileTitle}>{translate("Due Date")}</Typography>
                <Typography>{formatDate(invoice.dueDate)}</Typography>
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <Typography {...styles.mobileTitle}>{translate("Total Amount")}</Typography>
                <Typography>{`${invoice.invoiceTotalDisplay || "--"}`}</Typography>
            </DataTableCell>
            <StyledWrapper {...styles.mobileSeparator}></StyledWrapper>
            {payInvoicesUrl && (
                <DataTableCell {...mergeWithCss(styles.dataCell, styles.payInvoiceCell)} className="payInvoiceCell">
                    <Typography {...styles.mobileTitle}>{translate("Actions")}</Typography>
                    <Link {...styles.payInvoiceLink} href={payInvoicesUrl}>
                        {translate("Pay Invoice")}
                    </Link>
                </DataTableCell>
            )}
        </>
    );
};

export default connect(mapStateToProps)(InvoiceSummaryCard);
